import { useState, useEffect, useCallback, useRef } from "react";
import type { Envelope } from "../api/client";

interface UseQueryResult<T> {
  data: T | undefined;
  meta: { total_count?: number; cursor?: string | null } | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useQuery<T>(
  fetcher: () => Promise<Envelope<T>>,
  deps: unknown[] = [],
  options?: { pollInterval?: number; enabled?: boolean },
): UseQueryResult<T> {
  const enabled = options?.enabled ?? true;
  const [data, setData] = useState<T | undefined>();
  const [meta, setMeta] = useState<
    { total_count?: number; cursor?: string | null } | undefined
  >();
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetcherRef.current();
      setData(result.data);
      setMeta(result.meta);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setIsLoading(false);
    }
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!enabled) return;
    fetch();
  }, [fetch, enabled]);

  // Polling
  useEffect(() => {
    if (!options?.pollInterval || !enabled) return;
    const id = setInterval(fetch, options.pollInterval);
    return () => clearInterval(id);
  }, [fetch, options?.pollInterval, enabled]);

  return { data, meta, isLoading, error, refetch: fetch };
}

interface UseMutationResult<TData, TInput> {
  mutate: (input: TInput) => Promise<TData | undefined>;
  reset: () => void;
  isLoading: boolean;
  error: Error | null;
}

export function useMutation<TData, TInput = void>(
  mutator: (input: TInput) => Promise<Envelope<TData> | void>,
): UseMutationResult<TData, TInput> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: TInput) => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await mutator(input);
        return result && "data" in result ? result.data : undefined;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [mutator],
  );

  const reset = useCallback(() => {
    setError(null);
    setIsLoading(false);
  }, []);

  return { mutate, reset, isLoading, error };
}
